import Box from '@material-ui/core/Box';
import Drawer from '@material-ui/core/Drawer';
import IconButton from '@material-ui/core/IconButton';
import { makeStyles, Theme } from '@material-ui/core/styles';
import MenuIcon from '@material-ui/icons/Menu';
import CloseIcon from '@material-ui/icons/Close';
import { ReactNode, useContext } from 'react';
import { SidebarConfig, WbSidebarConfigContext } from '../../config';
import { useHomeUrl } from '../../../../hooks';
import { WbSidebarOpenStateProvider } from '../../WbSidebarOpenStateContext';
import { useWbSidebarMobileState } from '../../WbNavbarMobileStateContext';
import { getOnHoverScrollbarStyle } from '../../../../styles';
import { WbSidebarLogo } from '../WbSidebarLogo/WbSidebarLogo';

const useStyles = makeStyles<Theme, { sidebarConfig: SidebarConfig }>(
  theme => ({
    root: props => ({
      background: theme.palette.white,
      zIndex: theme.zIndex.appBar,
      paddingLeft: theme.spacing(0),
      paddingRight: theme.spacing(1.5),
      borderBottom: `1px solid ${theme.palette.grey[200]}`,
      [theme.breakpoints.up(props.sidebarConfig.mobileBreakpoint)]: {
        display: 'none',
      },
    }),
    navbar: {
      display: 'flex',
      alignItems: 'center',
      height: '32px',
    },
    drawer: props => ({
      width: '100%',
      height: '100%',
      [theme.breakpoints.up(props.sidebarConfig.mobileBreakpoint)]: {
        display: 'none',
      },
    }),
    drawerPaper: {
      width: '100%',
      background: theme.palette.white,
      boxShadow: theme.shadows[23],
      padding: theme.spacing(0),
    },
    drawerHeader: {
      height: '32px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      borderBottom: `1px solid ${theme.palette.grey[200]}`,
      padding: theme.spacing(0, 1.5),
      backgroundColor: theme.palette.white,
    },
    drawerContent: {
      display: 'flex',
      flexFlow: 'column nowrap',
      alignItems: 'flex-start',
      overflowX: 'hidden',
      height: '100%',
      padding: theme.spacing(0),
      ...getOnHoverScrollbarStyle(theme, false),
    },
    menuButtonCountainer: {
      width: '44px',
      height: '100%',
      borderRight: `1px solid ${theme.palette.grey[200]}`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
  }),
  { name: 'WitboostMobileNavbar' },
);

export type MobileNavbarProps = { children: ReactNode };

export const WbMobileNavbar = (props: MobileNavbarProps) => {
  const { sidebarConfig } = useContext(WbSidebarConfigContext);
  const classes = useStyles({ sidebarConfig });
  const { isMenuOpen, toggleMenu, setMenuOpen } = useWbSidebarMobileState();

  const { children } = props;
  const { homeURL, loading } = useHomeUrl();
  return (
    <nav className={classes.root} aria-label="mobile navigation">
      <WbSidebarOpenStateProvider
        value={{
          isOpen: true,
          setPinned: () => {},
          cancelPendingStateChange: () => {},
        }}
      >
        <div className={classes.navbar}>
          <Box className={classes.menuButtonCountainer}>
            <IconButton
              onClick={toggleMenu}
              color="inherit"
              aria-label="open menu"
              size="small"
            >
              <MenuIcon />
            </IconButton>
          </Box>

          {!loading && <WbSidebarLogo to={homeURL} />}
        </div>

        <Drawer
          anchor="left"
          open={isMenuOpen}
          onClose={() => setMenuOpen(false)}
          classes={{
            paper: classes.drawerPaper,
          }}
          className={classes.drawer}
        >
          <div className={classes.drawerHeader}>
            {!loading && <WbSidebarLogo to={homeURL} />}

            <IconButton
              onClick={toggleMenu}
              aria-label="close menu"
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </div>

          <Box className={classes.drawerContent}>{children}</Box>
        </Drawer>
      </WbSidebarOpenStateProvider>
    </nav>
  );
};
